import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiFieldComponent,
  QaiFloatingSelectComponent,
  QaiFloatingSelectOption,
  QaiMultiSelectComponent,
  QaiMultiSelectOption,
} from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { ITeamAssociation } from 'src/app/interfaces/self-serve/assignment-manager/responses/iuser-assignment-summary';
import { IAssignmentOption, IAddToAssignmentPayload } from 'src/app/interfaces/self-serve/assignment-manager/responses/iadd-to-assignment';
import { transformLabel } from 'src/app/utils/string-replacements';

@Component({
  selector: 'add-to-assignment-modal',
  standalone: true,
  templateUrl: './add-to-assignment-modal.component.html',
  styleUrls: ['./add-to-assignment-modal.component.scss'],
  imports: [
    QaiDialogComponent,
    QaiButtonComponent,
    QaiFieldComponent,
    QaiFloatingSelectComponent,
    QaiMultiSelectComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AddToAssignmentModalComponent {
  readonly dialogRef = viewChild<QaiDialogComponent>('dialog');

  readonly open        = input<boolean>(false);
  readonly assignments = input<IAssignmentOption[]>([]);
  readonly userTeams   = input<ITeamAssociation[]>([]);

  readonly closeModal = output<void>();
  readonly assigned   = output<IAddToAssignmentPayload>();

  readonly i18nKeys = {
    modalHeading:                 'simulationBuilder.ADD_TO_ASSIGNMENT_MODAL_HEADING',
    fieldLabelAssignment:         'simulationBuilder.FIELD_LABEL_ASSIGNMENT',
    searchAssignmentsPlaceholder: 'simulationBuilder.SEARCH_ASSIGNMENTS_PLACEHOLDER',
    buttonSetTeamAssociation:     'simulationBuilder.BUTTON_SET_TEAM_ASSOCIATION',
    fieldLabelTeam:               'simulationBuilder.FIELD_LABEL_TEAM',
    searchTeamPlaceholder:        'simulationBuilder.SEARCH_TEAM_PLACEHOLDER',
    buttonCancel:                 'simulationBuilder.BUTTON_CANCEL',
    buttonAddToAssignment:        'simulationBuilder.BUTTON_ADD_TO_ASSIGNMENT',
  };

  // ── Step ─────────────────────────────────────────────────────────────────
  readonly step = signal<1 | 2>(1);

  // ── Step 1 ────────────────────────────────────────────────────────────────
  readonly selectedAssignmentUuid = signal('');
  readonly selectedAssignmentName = signal('');

  readonly assignmentOptions = computed<QaiFloatingSelectOption[]>(() =>
    this.assignments().map(a => ({
      value: a.uuid,
      label: a.name,
      ...(a.status ? { description: transformLabel(a.status) } : {}),
    }))
  );

  // ── Step 2 ────────────────────────────────────────────────────────────────
  readonly selectedTeamUuids = signal<string[]>([]);
  readonly isSaving          = signal(false);

  readonly teamOptions = computed<QaiMultiSelectOption[]>(() =>
    this.userTeams().map(t => ({ value: t.uuid, label: t.name }))
  );

  readonly canProceed = computed(() => !!this.selectedAssignmentUuid());
  readonly canAssign  = computed(() => this.selectedTeamUuids().length > 0);

  constructor() {
    effect(() => {
      if (this.open()) {
        this.resetState();
        this.dialogRef()?.showModal();
      } else {
        this.dialogRef()?.close();
      }
    });
  }

  private resetState(): void {
    this.step.set(1);
    this.selectedAssignmentUuid.set('');
    this.selectedAssignmentName.set('');
    this.selectedTeamUuids.set([]);
    this.isSaving.set(false);
  }

  // ── Step 1 handlers ───────────────────────────────────────────────────────
  onAssignmentSelect(value: string): void {
    this.selectedAssignmentUuid.set(value);
    this.selectedAssignmentName.set(this.assignments().find(a => a.uuid === value)?.name ?? '');
  }

  onNextStep(): void {
    if (this.canProceed()) {
      this.step.set(2);
    }
  }

  // ── Step 2 handlers ───────────────────────────────────────────────────────
  onTeamSelectionChange(values: string[]): void {
    this.selectedTeamUuids.set(values);
  }

  onAssign(): void {
    if (!this.canAssign() || this.isSaving()) return;
    this.isSaving.set(true);
    this.assigned.emit({
      moduleAssignmentUuid: this.selectedAssignmentUuid(),
      teamUuids: this.selectedTeamUuids(),
    });
  }

  onCancel(): void {
    this.closeModal.emit();
  }
}
